<template>
  <span v-show="isActive" :class="classes" v-on="$listeners">
    <span class="chip__content">
      <slot />
      <div v-if="closable" class="chip__close" @click.stop="onClose">
        <Icon :dark="dark" image :size="16">
          {{ dark ? "/images/close-button-white.png" : "/images/close-button.png" }}
        </Icon>
      </div>
    </span>
  </span>
</template>

<script>
/**
 * Chip component that convey small piece of information.
 *
 * @vue-prop {Boolean} [closable] - Adds remove button.
 * @vue-prop {Boolean} [dark] - Applies the dark theme variant.
 * @vue-prop {Boolean} [disabled] - Disables the chip, making it un-selectable.
 * @vue-prop {Boolean} [label] - Removes circle edges.
 * @vue-prop {Boolean} [outline] - Removes background and applies border and text color.
 * @vue-prop {Boolean} [selected] - Applies a selection color to the chip.
 * @vue-prop {Boolean} [small] - Decreases the chip size.
 * @vue-prop {String} [textColor] - Applies a specified color to the control text.
 * @vue-prop {Boolean} [value=true] - Controls visibility.
 */
import Icon from '@/components/icon.vue';


export default {
  name: 'Chip',

  components: {
    Icon,
  },

  props: {
    closable: Boolean,
    dark: Boolean,
    disabled: Boolean,
    label: Boolean,
    outline: Boolean,
    // Used for selects/tagging
    selected: Boolean,
    small: Boolean,
    textColor: String,
    value: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isActive: !!this.value,
    };
  },

  computed: {
    isDark() {
      return this.dark === true;
    },
    isClickable() {
      return Boolean(this.$listeners.click);
    },
    classes() {
      return {
        chip: true,
        'chip--clickable': this.isClickable,
        'chip--disabled': this.disabled,
        'chip--selected': this.selected && !this.disabled,
        'chip--label': this.label,
        'chip--outline': this.outline,
        'chip--small': this.small,
        'chip--removable': this.closable,
        'theme--dark': this.isDark,
        'theme--light': !this.isDark,
      };
    },
  },

  watch: {
    value(val) {
      this.isActive = !!val;
    },
    isActive(val) {
      if (this.value !== !!val) {
        this.$emit('input', val);
      }
    },
  },

  methods: {
    onClose() {
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
@import "@/assets/scss/elevations.scss";

$chip-border-radius: 28px;
$chip-close-margin: 0 2px 0 8px;
$chip-font-size: 12px;
$chip-label-border-radius: 2px;
$chip-icon-font-size: 20px;
$chip-padding: 0 12px;
$chip-outline-background: transparent !important;
$chip-outline-color: #9e9e9e;
$chip-height: 32px;
// $chip-margin in vars.scss
$chip-small-height: 24px;
$chip-icon-offset: 8px;
$chip-icon-negative-offset: -8px;
$chip-removable-padding: 0 $chip-margin 0 $chip-icon-offset;


.theme--light.chip {
  background: #e0e0e0;
  color: rgba(0, 0, 0, 0.87);

  &--disabled {
    color: rgba(255, 255, 255, 0.5);
  }
}
.theme--dark.chip {
  background: #555;
  color: #fff;

  &--disabled {
    color: rgba(255, 255, 255, 0.5);
  }
}

.chip {
  align-items: center;
  border-radius: $chip-border-radius;
  cursor: default;
  display: inline-flex;
  font-size: $chip-font-size;
  margin: $chip-margin;
  outline: none;
  position: relative;
  transition: $primary-transition;
  vertical-align: middle;

  .chip__content {
    align-items: center;
    border-radius: $chip-border-radius;
    display: inline-flex;
    height: $chip-height;
    justify-content: space-between;
    overflow: hidden;
    padding: $chip-padding;
    vertical-align: middle;
    white-space: nowrap;
    z-index: 1;
  }
  &--clickable {
    cursor: pointer;
  }
  &--removable {
    .chip__content {
      padding: $chip-removable-padding;
    }
  }
  &:focus:not(.chip--disabled),
  &--active,
  &--selected {
    border-color: rgba(#000, .13);
    @include elevation(2);

    &:after {
      background: currentColor;
      border-radius: inherit;
      content: '';
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      transition: inherit;
      width: 100%;
      pointer-events: none;
      opacity: .13;
    }
  }
  &--label {
    border-radius: $chip-label-border-radius;

    .chip__content {
      border-radius: $chip-label-border-radius;
    }
  }
  // Increase specificity to override theme background
  &\&.chip--outline {
    background: $chip-outline-background;
    border: 1px solid currentColor;
    color: $chip-outline-color;
    height: $chip-height;
  }
  &--small {
    height: $chip-small-height !important;
  }
  &__close {
    align-items: center;
    color: inherit;
    display: flex;
    flex: 0 0 auto;
    font-size: $chip-icon-font-size;
    margin: $chip-close-margin;
    text-decoration: none;
    user-select: none;

    > .icon {
      color: inherit !important;
      font-size: 20px;
      cursor: pointer;
      opacity: .5;

      &:hover {
        opacity: 1;
      }
    }
  }
  &--disabled {
    .chip__close {
      pointer-events: none;
    }
  }
  .icon {
    color: inherit
  }
}
</style>
